import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Target, TrendingUp, TrendingDown, Info } from "lucide-react";
import { formatLKR } from "@/lib/api";
import ValuationBadge from "@/components/common/ValuationBadge";

/**
 * TargetPriceCard — Phase 8 (Item 26)
 *
 * Multi-method 12-month target price summary. Shows four independent
 * intrinsic-value estimates (PE-based, PB-based, DDM, Graham), the
 * average, and the implied upside/downside vs current price.
 *
 * The four methods deliberately vary in their assumptions so investors
 * can triangulate. We anchor PE and PB to long-term CSE blue-chip
 * medians (15× and 1.5×). DDM uses 12% required return, 5% growth.
 * Graham number uses sqrt(22.5 × EPS × BVPS).
 */

const Method = ({ label, formula, value, current, testId }) => {
  const has = value != null && value > 0;
  const upPct =
    has && current && current > 0
      ? ((value - current) / current) * 100
      : null;
  const tone =
    upPct == null
      ? "text-muted-foreground"
      : upPct >= 0
        ? "text-[hsl(var(--success))]"
        : "text-[hsl(var(--destructive))]";
  return (
    <div
      className="rounded-md border bg-card/60 p-3 space-y-1"
      data-testid={testId}
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
          {label}
        </span>
        <span className={`text-[11px] tabular-nums ${tone}`}>
          {upPct == null ? "—" : `${upPct >= 0 ? "+" : ""}${upPct.toFixed(1)}%`}
        </span>
      </div>
      <div className="font-display text-xl tabular-nums leading-tight">
        {has ? formatLKR(value) : <span className="text-muted-foreground">—</span>}
      </div>
      <div className="text-[10px] font-mono text-muted-foreground/80">{formula}</div>
    </div>
  );
};

const TargetPriceCard = ({ score }) => {
  if (!score) return null;
  const price = score.current_price ?? null;
  const targetAvg = score.target_average ?? null;
  // Test 6 Fix 1 — headline = canonical weighted base case (PE 40% + PB 40%
  // + DDM 20%) so it matches the screener and suggestion cards exactly.
  // Falls back to the simple average for legacy docs.
  const targetBase = score.target_base_case ?? targetAvg;
  const upside =
    score.target_base_case_upside_pct != null
      ? score.target_base_case_upside_pct
      : score.target_upside_pct ?? null;
  const used = score.target_methods_used ?? 0;

  const isTrust = !!score.is_investment_trust;
  if (isTrust) {
    return (
      <Card className="border-2 border-border bg-muted/40" data-testid="target-price-card">
        <CardHeader className="pb-2">
          <CardTitle className="font-display text-base flex items-center gap-2">
            <Target className="h-4 w-4 text-primary" />
            Target Price Estimates
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground leading-snug">
            Multi-method targets do not apply to investment holding companies.
            Use NAV discount and portfolio quality instead.
          </p>
        </CardContent>
      </Card>
    );
  }

  const upsidePositive = upside != null && upside > 0;
  const headlineTone =
    upside == null
      ? "border-border"
      : upsidePositive
        ? "border-[hsl(var(--success))]/50"
        : "border-[hsl(var(--destructive))]/50";

  return (
    <Card
      className={`border-2 ${headlineTone} bg-card`}
      data-testid="target-price-card"
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="font-display text-base flex items-center gap-2">
              <Target className="h-4 w-4 text-primary" />
              Target Price Estimates
              <Badge variant="secondary" className="text-[10px] font-normal">
                {used}/4 methods
              </Badge>
            </CardTitle>
            <p className="text-[11px] text-muted-foreground mt-1">
              Base case = weighted average (PE 40% + PB 40% + DDM 20%).
              Same number shown on the screener and suggestion cards.
            </p>
          </div>
          {targetBase != null && (
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Base case target
              </div>
              <div
                className="font-display text-2xl tabular-nums font-semibold leading-tight"
                data-testid="target-base-case-headline"
              >
                {formatLKR(targetBase)}
              </div>
              {upside != null && (
                <div
                  className={`text-xs tabular-nums font-medium inline-flex items-center gap-1 ${
                    upsidePositive
                      ? "text-[hsl(var(--success))]"
                      : "text-[hsl(var(--destructive))]"
                  }`}
                  data-testid="target-upside-pct"
                >
                  {upsidePositive ? (
                    <TrendingUp className="h-3 w-3" />
                  ) : (
                    <TrendingDown className="h-3 w-3" />
                  )}
                  {upsidePositive ? "+" : ""}
                  {upside.toFixed(1)}% vs price
                </div>
              )}
              {targetAvg != null && targetAvg !== targetBase && (
                <div className="text-[10px] text-muted-foreground mt-1">
                  4-method avg: {formatLKR(targetAvg)}
                </div>
              )}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        {/* Valuation Verdict — narrative summary above the 4-method grid. */}
        {score.valuation_status && score.valuation_status !== "no_data" && (
          <div
            className="rounded-md border bg-muted/30 p-3 space-y-1.5"
            data-testid="target-card-valuation-verdict"
          >
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Valuation verdict:
              </span>
              <ValuationBadge
                status={score.valuation_status}
                discountPct={score.valuation_discount_pct}
                size="sm"
              />
            </div>
            <p className="text-xs text-muted-foreground leading-snug">
              {(() => {
                const dp = score.valuation_discount_pct;
                const fv = score.fair_value_used;
                const px = score.current_price;
                const basis =
                  score.fair_value_basis === "graham"
                    ? "Graham Number"
                    : "base case fair value";
                if (dp == null || fv == null || px == null) return null;
                const absPct = Math.abs(dp).toFixed(0);
                if (dp > 30) {
                  return (
                    <>
                      Current price <strong>{formatLKR(px)}</strong> is{" "}
                      <strong>{absPct}% below</strong> our {basis} of{" "}
                      <strong>{formatLKR(fv)}</strong>. This represents a{" "}
                      <em>significant margin of safety</em> for long-term investors.
                    </>
                  );
                }
                if (dp > 10) {
                  return (
                    <>
                      Current price <strong>{formatLKR(px)}</strong> is{" "}
                      <strong>{absPct}% below</strong> our {basis} of{" "}
                      <strong>{formatLKR(fv)}</strong>. This represents a meaningful
                      margin of safety for long-term investors.
                    </>
                  );
                }
                if (dp >= -10) {
                  return (
                    <>
                      Current price <strong>{formatLKR(px)}</strong> is broadly in line
                      with our {basis} of <strong>{formatLKR(fv)}</strong> ({dp >= 0 ? "+" : ""}
                      {dp.toFixed(1)}% gap). Limited margin of safety either way.
                    </>
                  );
                }
                if (dp >= -30) {
                  return (
                    <>
                      Current price <strong>{formatLKR(px)}</strong> is{" "}
                      <strong>{absPct}% above</strong> our {basis} of{" "}
                      <strong>{formatLKR(fv)}</strong>. Re-entry on a pullback may offer
                      better risk/reward.
                    </>
                  );
                }
                return (
                  <>
                    Current price <strong>{formatLKR(px)}</strong> is{" "}
                    <strong>{absPct}% above</strong> our {basis} of{" "}
                    <strong>{formatLKR(fv)}</strong>. Significant downside risk if
                    mean-reversion to fair value occurs.
                  </>
                );
              })()}
            </p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2.5">
          <Method
            label="PE anchor (15×)"
            formula="15 × EPS"
            value={score.target_pe_based}
            current={price}
            testId="target-method-pe"
          />
          <Method
            label="PB anchor (1.5×)"
            formula="1.5 × BVPS"
            value={score.target_pb_based}
            current={price}
            testId="target-method-pb"
          />
          <Method
            label="DDM"
            formula="DPS ÷ (12% − 5%)"
            value={score.target_ddm}
            current={price}
            testId="target-method-ddm"
          />
          <Method
            label="Graham"
            formula="√(22.5 × EPS × BVPS)"
            value={score.target_graham}
            current={price}
            testId="target-method-graham"
          />
        </div>

        <div className="flex items-start gap-2 rounded-md border border-border/70 bg-muted/40 p-2.5 text-[11px] leading-snug text-muted-foreground">
          <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>
            Reference points only — not buy/sell recommendations. Wide
            spreads between methods often indicate uncertainty (e.g. high
            growth or unstable earnings). The PE/PB anchors are CSE blue-chip
            long-term medians and may differ for your sector.
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

export default TargetPriceCard;
